import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ClipSpinner } from '~/components/ui';
import { useGetOrderById, useGetOrderMsrSyncStatus } from '~/api';
import {
  useMsrSyncRefetchInterval,
  useMsrSyncToast,
} from '~/hooks/useMsrSyncPolling';
import { hasText } from '~/utils';
import { BuyerOrderView } from '~/pages/order-view/sections/BuyerOrderView';
import { SellerOrderView } from '~/pages/order-view/sections/SellerOrderView';
import { Navigate, useParams } from 'react-router';

export function OrderView() {
  const { orderId } = useParams();

  if (!orderId) {
    return <Navigate to="/dashboard" replace />;
  }

  return <OrderViewContent orderId={orderId} />;
}

function OrderViewContent({ orderId }: { orderId: string }) {
  const { t } = useTranslation('order');
  // useGetOrderById's refetchInterval option needs to know whether the buying org even uses MSR
  // (no polling otherwise), but that flag only exists in useGetOrderById's own result.
  // lastKnownMsrSyncEnabled bridges the two, adjusted during render (React's documented pattern)
  // rather than via a ref, since a ref read here would be a stale snapshot the compiler can't
  // track across renders.
  const [lastKnownMsrSyncEnabled, setLastKnownMsrSyncEnabled] = useState(false);
  const refetchInterval = useMsrSyncRefetchInterval(
    lastKnownMsrSyncEnabled,
    orderId
  );
  const { data, isLoading, isError, error } = useGetOrderById(orderId, {
    refetchInterval,
  });
  const msrSyncEnabled = Boolean(
    data?.order?.buyingOrganization.msrSyncEnabled
  );
  if (msrSyncEnabled !== lastKnownMsrSyncEnabled) {
    setLastKnownMsrSyncEnabled(msrSyncEnabled);
  }
  const hasPurchaseOrderNumber = hasText(data?.order?.purchaseOrderNumber);
  const { data: msrSyncStatus, dataUpdatedAt: msrSyncFetchedAt } =
    useGetOrderMsrSyncStatus(orderId, {
      enabled: msrSyncEnabled && hasPurchaseOrderNumber,
      refetchInterval,
    });
  useMsrSyncToast(
    orderId,
    msrSyncStatus?.order?.updatedAt,
    msrSyncStatus?.order?.purchaseOrderNumber,
    msrSyncFetchedAt
  );

  if (isLoading) return <ClipSpinner />;
  if (isError)
    return <div>{t('order.loading_error', { message: error.message })}</div>;

  if (data?.order?.viewerPerspective === 'BUYING') {
    return <BuyerOrderView orderId={orderId} />;
  }
  return <SellerOrderView orderId={orderId} />;
}
